import { excludeKeys } from '@repo/common-utils';
import { PageViewEventLogger } from '@repo/tracking-service';
import { AuthGuard } from '@repo/user-session';
import { GetServerSideProps } from 'next';
import { ReactElement, useEffect } from 'react';

import { SectionScrollModelProvider, useSectionScrollModel } from '@/components/common/context/SectionScrollModelProvider';
import Layout from '@/components/layout/Layout';
import { ApplyCompleteContent } from '@/components/tax-refund/apply/complete/ApplyCompleteContent';
import { useRightInquiryTextButton } from '@/components/tax-refund/common/hooks/use-create-header-props';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { useApplyResultData } from '@/lib/hooks/use-apply-result-data';
import { useSnapshotData } from '@/lib/hooks/use-snapshot-data';
import { BusinessType } from '@/lib/types/request';

const ApplyCompletePage = ({ businessType }: { businessType: BusinessType }) => {
  const { eventAttribute } = useSectionScrollModel();
  const rightInquiryButton = useRightInquiryTextButton(
    SUPPORT_BOT_LINK.신청완료,
    businessType === 'indis' ? 'indis-request_apply-complete_cx' : 'corps-request_apply-complete_cx'
  );
  const { saveSnapshotData } = useSnapshotData('apply');
  const { applyResultData } = useApplyResultData(businessType);

  useEffect(() => {
    const refundId = applyResultData?.taxType === 'income' ? applyResultData?.refundId : applyResultData?.tritxRefundInfo?.refundId;
    if (refundId && (applyResultData?.refundStatus === 'ApplyComplete' || applyResultData?.tritxRefundInfo?.refundStatus === 'ApplyComplete')) {
      saveSnapshotData(refundId, applyResultData);
    }
  }, [applyResultData, saveSnapshotData]);

  return (
    <PageViewEventLogger
      pageName={businessType === 'indis' ? 'indis-request_apply-complete' : 'corps-request_apply-complete'}
      eventProperty={excludeKeys(eventAttribute ?? {}, ['current_section'])}
    >
      <Layout rightTextButton={rightInquiryButton}>
        <ApplyCompleteContent businessType={businessType} />
      </Layout>
    </PageViewEventLogger>
  );
};

export default ApplyCompletePage;

export const getServerSideProps = (async ({ query }) => {
  if (query?.type) {
    return { props: { businessType: query?.type as BusinessType } };
  }
  return { props: { businessType: '' } };
}) satisfies GetServerSideProps;

ApplyCompletePage.getLayout = (page: ReactElement) => (
  <AuthGuard>
    <SectionScrollModelProvider>{page}</SectionScrollModelProvider>
  </AuthGuard>
);
